import {
  AiStar,
  COCDismissIcon,
  CrossFillIcon,
  GreenTickIcon,
} from '@/assets/icons/icons';
import { CustomParagraph } from '@/components';
import { Avatar, Col, Divider, Flex, Row } from 'antd';
import { useMemo, useState } from 'react';
import { getFormattedTime } from '@/common/utils/getFormattedTimeUtils';
import { getInitials, useToken } from '@/common/helper';
import dayjs from 'dayjs';

interface ActivityFeedItem {
  id: string | number;
  title?: string;
  comment?: string | null;
  name?: string;
  timestamp?: string;
  is_ai?: boolean;
  ai_type?: string | null;
  status?: 'Resolved' | 'Not Resolved' | 'Dismissed' | string;
  author_badge_color?: string;
}

interface EmailCallActionProps {
  alertActions?: ActivityFeedItem[];
}

const EmailCallActions = ({ alertActions }: EmailCallActionProps) => {
  const { decodedUserToken } = useToken();
  const isTimeZone = decodedUserToken()?.time_zone_enable;
  const dateFormat = decodedUserToken()?.date_format;
  const [showFull, setShowFull] = useState(false);

  const userColorMap = useMemo(() => {
    const map: Record<string, string> = {};

    alertActions?.forEach((action: ActivityFeedItem) => {
      const user = action?.name;
      if (user && !map[user]) {
        map[user] = action?.author_badge_color || '#ccc';
      }
    });

    return map;
  }, [alertActions]);

  return (
    <>
      <Divider style={{ background: 'var(--gray-300)' }} />
      {[...(alertActions || [])]
        .sort(
          (a, b) => dayjs(a.timestamp).valueOf() - dayjs(b.timestamp).valueOf()
        )
        .map((action, index) => {
          const hasComment =
            !!(action?.comment && String(action.comment).trim()) &&
            action?.title?.trim()?.toLowerCase() !==
              'resolved by ai assistant' &&
            action?.ai_type !== 'email';

          return (
            <Row key={index} align='top' gutter={12}>
              <Col span={3}>
                <Flex gap={3} justify='center' align='center'>
                  {action?.is_ai ? (
                    <Flex
                      style={{
                        width: 36,
                        height: 36,
                        marginTop: hasComment ? 8 : 0,
                      }}
                      align='center'
                      justify='center'
                    >
                      <AiStar
                        color='var(--text-primary)'
                        width={16}
                        height={16}
                        style={{ display: 'block' }}
                      />
                    </Flex>
                  ) : (
                    <>
                      {!action?.name ? (
                        <div className={`one-time-action-type-icon`}>
                          {action?.status === 'Resolved' ? (
                            <GreenTickIcon />
                          ) : action?.status === 'Not Resolved' ||
                            action?.status === 'Needs attention' ? (
                            <CrossFillIcon />
                          ) : (
                            <COCDismissIcon />
                          )}
                        </div>
                      ) : (
                        <Avatar
                          size={36}
                          className='reply-avatar flex-center y'
                          style={{
                            fontSize: '14px',
                            backgroundColor:
                              userColorMap[action?.name] || '#ccc',
                            marginTop: hasComment ? 8 : 0,
                          }}
                        >
                          {getInitials(action?.name)}
                        </Avatar>
                      )}
                    </>
                  )}
                </Flex>
              </Col>

              <Col span={21} style={{ paddingLeft: '0px' }}>
                <div
                  className='flex-center btw y w-full relative gap-x-1'
                  style={{ minHeight: !hasComment ? '36px' : undefined }}
                >
                  <Flex
                    align='center'
                    justify='space-between'
                    className='w-full'
                  >
                    <CustomParagraph
                      text={action?.title || '--'}
                      fontSize='14px'
                      fontWeight={600}
                      lineHeight='16px'
                      color='var(--text-primary)'
                      marginBottom='0'
                      whiteSpace='pre-line'
                    />

                    <span
                      onClick={() => setShowFull((prev) => !prev)}
                      style={{ cursor: 'pointer' }}
                    >
                      <CustomParagraph
                        text={
                          showFull
                            ? getFormattedTime(
                                action?.timestamp,
                                isTimeZone,
                                dateFormat
                              )?.full
                            : getFormattedTime(
                                action?.timestamp,
                                isTimeZone,
                                dateFormat
                              )?.relative || '--'
                        }
                        fontSize='12px'
                        fontWeight='400'
                        lineHeight='16px'
                        color='var(--text-light)'
                        marginBottom='0'
                        style={{ whiteSpace: 'nowrap' }}
                      />
                    </span>
                  </Flex>
                </div>

                {hasComment && (
                  <CustomParagraph
                    text={String(action?.comment || '')}
                    fontSize='12px'
                    fontWeight='400'
                    lineHeight='20px'
                    color='black'
                    marginTop={8}
                    marginBottom='4px'
                  />
                )}

                {alertActions?.length - 1 !== index && (
                  <Divider
                    style={{ margin: '16px 0', background: 'var(--gray-300)' }}
                  />
                )}
              </Col>
            </Row>
          );
        })}
    </>
  );
};

export default EmailCallActions;
